import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Download, DollarSign, Trash2, FileText, Pencil } from "lucide-react";
import { api, downloadBlob } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/DatePicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sent: "bg-blue-50 text-blue-700 border-blue-200",
  overdue: "bg-rose-50 text-rose-700 border-rose-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
};

const PaidCell = ({ inv }) => {
  const dates = (inv.payments || []).map((p) => p.date).filter(Boolean).sort();
  if (!dates.length) return <span className="text-slate-300">—</span>;
  const last = dates[dates.length - 1];
  const late = Math.round((new Date(last) - new Date(inv.due_date)) / 86400000);
  return (
    <div data-testid={`invoice-paid-date-${inv.id}`}>
      <span className={late > 0 ? "text-rose-600 font-medium" : "text-slate-600"}>{fmtDate(last)}</span>
      {late > 0 && <div className="text-xs text-rose-500">{late} day{late > 1 ? "s" : ""} late</div>}
    </div>
  );
};

export default function Invoices() {
  const { currentId } = useCompany();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ amount: "", date: todayISO(), method: "manual", note: "" });

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => {
    if (currentId) load();
  }, [currentId]);

  const openPay = (inv) => {
    setPayFor(inv);
    setPay({ amount: String(inv.unrecorded ?? inv.balance_due), date: todayISO(), method: "manual", note: "" });
  };

  const changePaymentDate = async (pid, date) => {
    try {
      const r = await api.patch(`/invoices/${payFor.id}/payments/${pid}`, { date });
      setPayFor({ ...payFor, payments: r.data.payments });
      toast.success("Payment date updated");
      load();
    } catch (e) {
      toast.error("Could not update payment date");
    }
  };

  const submitPay = async () => {
    const amount = parseFloat(pay.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post(`/invoices/${payFor.id}/payments`, { ...pay, amount });
      toast.success("Payment recorded");
      setPayFor(null);
      load();
    } catch (e) {
      toast.error("Could not record payment");
    }
  };

  const downloadPdf = async (inv) => {
    try {
      await downloadBlob(`/invoices/${inv.id}/pdf`, `${inv.invoice_number}.pdf`);
    } catch (e) {
      toast.error("Could not generate PDF");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Invoice deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Invoices</h1>
          <p className="text-slate-500 text-sm mt-1">5% GST auto-applied · download PDF · record payments.</p>
        </div>
        <Button
          data-testid="new-invoice-btn"
          onClick={() => navigate("/invoices/new")}
          className="rounded-md bg-slate-900 hover:bg-slate-800"
        >
          <Plus className="h-4 w-4 mr-2" /> New Invoice
        </Button>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        {invoices.length === 0 ? (
          <div className="p-16 text-center">
            <FileText className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-slate-400 mt-3 text-sm">No invoices yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-5 py-3 font-medium">Invoice</th>
                <th className="px-5 py-3 font-medium">Client</th>
                <th className="px-5 py-3 font-medium">Issued</th>
                <th className="px-5 py-3 font-medium">Due</th>
                <th className="px-5 py-3 font-medium">Paid</th>
                <th className="px-5 py-3 font-medium text-right">GST</th>
                <th className="px-5 py-3 font-medium text-right">Total</th>
                <th className="px-5 py-3 font-medium text-right">Balance</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{inv.invoice_number}</td>
                  <td className="px-5 py-3 text-slate-600">{inv.client_name}</td>
                  <td className="px-5 py-3 text-slate-600">{fmtDate(inv.issue_date)}</td>
                  <td className="px-5 py-3 text-slate-600">{fmtDate(inv.due_date)}</td>
                  <td className="px-5 py-3"><PaidCell inv={inv} /></td>
                  <td className="px-5 py-3 text-right tabular-nums text-slate-600">{money(inv.gst_amount)}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-medium">{money(inv.total)}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{money(inv.balance_due)}</td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className={`capitalize ${STATUS_STYLE[inv.display_status]}`}>
                      {inv.display_status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" title="Edit" data-testid={`edit-invoice-${inv.id}`} onClick={() => navigate(`/invoices/${inv.id}`)}>
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Download PDF" data-testid={`pdf-invoice-${inv.id}`} onClick={() => downloadPdf(inv)}>
                      <Download className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Payments"
                      data-testid={`pay-invoice-${inv.id}`}
                      onClick={() => openPay(inv)}
                    >
                      <DollarSign className="h-4 w-4 text-emerald-600" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Delete" data-testid={`delete-invoice-${inv.id}`} onClick={() => remove(inv.id)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Payments · {payFor?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {payFor?.payments?.length > 0 && (
              <div data-testid="payments-list" className="border border-slate-200 rounded-md divide-y divide-slate-100">
                {payFor.payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`payment-row-${p.id}`}>
                    <div className="w-44">
                      <DatePicker testId={`payment-edit-date-${p.id}`} value={p.date} onChange={(d) => d && d !== p.date && changePaymentDate(p.id, d)} />
                    </div>
                    <span className="tabular-nums font-medium text-slate-900">{money(p.amount)}</span>
                    <span className="text-slate-500 truncate">{p.note}</span>
                  </div>
                ))}
              </div>
            )}
            {payFor?.unrecorded > 0.001 ? (
            <>
            <div className="text-sm text-slate-500">
              {payFor?.display_status === "paid" ? "Marked paid but not recorded:" : "Balance due:"}{" "}
              <span className="font-medium text-slate-900 tabular-nums">{money(payFor?.unrecorded)}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="payment-amount-input"
                  value={pay.amount}
                  onChange={(e) => setPay({ ...pay, amount: e.target.value })}
                  className="mt-1 rounded-md"
                />
              </div>
              <div>
                <Label>Date</Label>
                <div className="mt-1">
                  <DatePicker testId="payment-date-picker" value={pay.date} onChange={(d) => setPay({ ...pay, date: d })} />
                </div>
              </div>
            </div>
            <div>
              <Label>Method / Note</Label>
              <Input
                data-testid="payment-note-input"
                value={pay.note}
                onChange={(e) => setPay({ ...pay, note: e.target.value })}
                placeholder="e.g. Stripe transfer, e-transfer, cheque"
                className="mt-1 rounded-md"
              />
            </div>
            </>
            ) : (
              <p className="text-sm text-slate-500" data-testid="payments-fully-recorded">Fully paid. Change a date above to fix when it was received.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setPayFor(null)}>
              Close
            </Button>
            {payFor?.unrecorded > 0.001 && (
            <Button data-testid="submit-payment-btn" onClick={submitPay} className="rounded-md bg-slate-900 hover:bg-slate-800">
              Record Payment
            </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
